import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * Explicit override map: product handle -> approved "Startseite" white-background
 * image URL. Used ONLY to correct the product image shown on matched cards inside
 * the recommendations section (the /recommendations/products endpoint does not
 * reflect theme-preview content). Does not affect which products are recommended,
 * their order, count, titles, prices, links, or layout.
 * @type {Record<string, string>}
 */
const RECOMMENDATION_IMAGE_OVERRIDES = {
  'cloudrest-pro':
    'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/ClaudRestPro_Startseite_weiss.png?v=1789067969',
  'ergonomische-babytrage':
    'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/CarryEase_Hip_Startseite_weiss.png?v=1789067968',
  'coolpaws™-kuhlende-liegeunterlage-fur-haustiere-1':
    'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/CoolPaws_Startseite_weiss.png?v=1789067970',
  toeflow: 'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/ToeFlow_Startseite_weiss.png?v=1789067971',
  pickleballset:
    'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/Pickleball_Pro_sEt_Startseite_weiss.png?v=1789067972',
  'happycat-activity-box':
    'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/HappyCat_Activity_Box_Startseite_weiss.png?v=1789067971',
  'airflow-pro-nebulizer':
    'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/AirFlow_Pro_Nebulizer_Startseite_weiss.png?v=1789067968',
  'comfortnest-pillow':
    'https://cdn.shopify.com/s/files/1/0950/5810/0554/files/ComfortNest_Pillow_Startseite_weiss.png?v=1789068150',
};

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;
      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          this.#applyRecommendationImageOverrides();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Corrects the product image on specific recommendation cards, after the fetched
   * recommendation HTML has been morphed into the DOM. Scoped strictly to this
   * <product-recommendations> element. Products are identified via their product
   * link/handle (not visible title text). Only the <img> src/srcset of matched
   * cards is touched; nothing else about the card (order, count, title, price,
   * link, layout) is changed.
   */
  #applyRecommendationImageOverrides() {
    const cards = this.querySelectorAll('product-card');

    cards.forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;

      let handle = null;
      try {
        const path = new URL(link.getAttribute('href'), window.location.origin).pathname;
        const match = path.match(/\/products\/([^/?#]+)/);
        handle = match ? decodeURIComponent(match[1]) : null;
      } catch (e) {
        handle = null;
      }

      if (!handle || !(handle in RECOMMENDATION_IMAGE_OVERRIDES)) return;

      const img = card.querySelector('img.product-media__image') || card.querySelector('img');
      if (!img) return;

      img.setAttribute('src', RECOMMENDATION_IMAGE_OVERRIDES[handle]);
      img.removeAttribute('srcset');
    });
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
